/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext } from "react";

import { useFormik } from "formik";

import ClayForm, {
  ClayRadio, ClayRadioGroup, ClayInput, ClaySelectWithOption,
} from "@clayui/form";

import ClayButton from "@clayui/button";
import ClayDatePicker from "@clayui/date-picker";
import ClayIcon from "@clayui/icon";
import ClayModal, { useModal } from "@clayui/modal";

import * as flags from "country-flag-icons/react/3x2";

import { toDate, formatInTimeZone } from "date-fns-tz";

import * as Yup from "yup";

import CountrySelect from "../Form/CountrySelect";

import { translate } from "../../i18n";

import ApiClient from "../../utils/ApiClient";
import Liferay from "../../utils/Liferay";

import useDateFormatter from "../../hooks/useDateFormatter";

import WizardContext from "./context/WizardContext";

/**
 * Componente principal de registro de médico.
 *
 * @returns Component react.
 */
function AppointmentForm() {

  // ======================
  // Estados del componente
  // ======================

  const [loading, setLoading] = useState(true);
  const [spinner, setSpinner] = useState(false);

  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // ===============
  // Uso de contexto
  // ===============

  const {
    extraInfo,
    localSlot,
    appointmentData,
    persistentData,
    setStep,
    setPersistentData,
  } = useContext(WizardContext);

  // ===================================
  // Hooks para formateo de fechas/horas
  // ===================================

  const { formatDate } = useDateFormatter();

  // =======================================
  // Hook para uso del modal de confirmación
  // =======================================

  const { observer, onOpenChange, open } = useModal();

  // ================================
  // Hook para uso del modal de éxito
  // ================================

  const {
    observer: observerConfirmation,
    onOpenChange: onOpenChangeConfirmation,
    open: openConfirmation,
  } = useModal();

  // =====================
  // Expresiones regulares
  // =====================

  const REGEXP = {
    ALPHABETICAL: /^[-'a-zA-ZÀ-ÖØ-öø-ÿ\s]+$/,
    NUMERIC: /^\d+$/,
    LENGTH: /^\d{10,15}$/,
    PASSWORD: /^(?=.*[A-Z])(?=.*\d)(?=.*[¡!¿?-_+.:,;"@#$%/^&*()[\]{}])(?=.*[a-z]).{8,}$/,
    DATE: /^\d{2}([/-])\d{2}\1\d{4}$/,
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  };

  // ======================
  // Constantes de dias y meses
  // ======================
  const MONTHS = [
    translate("january"),
    translate("february"),
    translate("march"),
    translate("april"),
    translate("may"),
    translate("june"),
    translate("july"),
    translate("august"),
    translate("september"),
    translate("october"),
    translate("november"),
    translate("december"),
  ];

  const DAYS = [
    translate("sunday"),
    translate("monday"),
    translate("tuesday"),
    translate("wednesday"),
    translate("thursday"),
    translate("friday"),
    translate("saturday"),
  ];

  // ====================
  // Definición de Formik
  // ====================

  const formik = useFormik({
    initialValues: {
      personalConsultation: true,
      firstName: "",
      firstSurname: "",
      gender: "",
      birthDate: "",
      email: "",
      countryCode: "",
      phoneNumber: "",
      bondWithPatient: "",
      message: "",
      paymentMethod: "",
    },
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      personalConsultation: Yup.boolean()
        .required(translate("field-required")),
      firstName: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("invalid-characters"),
        )
        .required(translate("field-required")),
      firstSurname: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("invalid-characters"),
        )
        .required(translate("field-required")),
      gender: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("invalid-characters"),
        )
        .required(translate("field-required")),
      birthDate: Yup.string().required(translate("field-required")),
      email: Yup.string()
        .matches(
          REGEXP.EMAIL,
          translate("invalid-email"),
        )
        .required(translate("field-required")),
      countryCode: Yup.mixed().required(),
      phoneNumber: Yup.string()
        .matches(
          REGEXP.NUMERIC,
          translate("invalid-characters"),
        )
        .matches(
          REGEXP.LENGTH,
          translate("invalid-characters"),
        )
        .required(translate("field-required")),
      message: Yup.string().required(translate("field-required")),
      paymentMethod: Yup.string().required(translate("field-required")),
      bondWithPatient: Yup.string()
        .when("personalConsultation", {
          is: (val) => val === false,
          then: () => Yup.string().required(translate("field-required")),
        }),
    }),
    onSubmit: async (values) => {

      try {

        setSpinner(true);

        let address = "";
        let officeId = 0;

        if (appointmentData.r_officeAgendaConfiguration_c_doctorsOffice) {

          address = appointmentData.r_officeAgendaConfiguration_c_doctorsOffice.address || "";
          officeId = appointmentData?.r_officeAgendaConfiguration_c_doctorsOffice?.id || 0;

        }

        const birthDateParse = Date.parse(values.birthDate);

        const rawDate = new Date(localSlot.rawDate);
        const hours = rawDate.getUTCHours();
        const minutes = rawDate.getUTCMinutes().toString().padStart(2, "0");
        const appointmentHour = ` ${hours}:${minutes} `;

        const createAppointmentRequest = {
          name: values.firstName,
          lastName: values.firstSurname,
          drNowUserId: parseInt(Liferay.ThemeDisplay.getUserId(), 10),
          email: values.email,
          birthDate: new Date(birthDateParse),
          countryCode: parseInt(values.countryCode.split(" ")[1], 10),
          phoneNumber: parseInt(values.phoneNumber, 10),
          startDate: localSlot.startdate,
          endDate: localSlot.endDate,
          r_medicalAppointments_c_doctorId: extraInfo.doctorData.id,
          r_officeMedicalAppointments_c_doctorsOfficeId: officeId,
          consultationType: appointmentData.consultationType.key,
          officeAddress: address,
          reason: values.message,
          appointmentOwnerUser: values.personalConsultation,
          relationshipWithPatient: values.bondWithPatient,
          paymentMethod: values.paymentMethod,
          price: appointmentData.consultationType?.name === "Online"
            ? extraInfo.onlinePrice : extraInfo.price,
          currency: extraInfo.currency,
          appointmentDate: formatDate(toDate(localSlot.rawDate)),
          appointmentTime: appointmentHour,
        };

        await ApiClient.createAppointment(createAppointmentRequest);

        onOpenChange(false);
        onOpenChangeConfirmation(true);

      } catch (error) {

        Liferay.Util.openToast({
          title: "",
          message: `
            <h4 class='font-weight-medium m-0'>
              ${translate("service-error")}
            </h4>
            <span class='text-3'>
              ${translate("please-try-again-later")}
            </span>
          `,
          type: "danger",
          toastProps: {
            hideCloseIcon: true,
          },
        });

      } finally {

        setSpinner(false);

      }

    },
  });

  // ========================
  // Funciones del componente
  // ========================

  /**
   * Función que transforma el modelo de catálogos al modelo de ClaySelectWithOption.
   *
   * @param catalog - Catálogo a transformar.
   *
   * @returns Catálogo transformado.
   */
  function catalogsMapper(catalog) {

    if (!catalog) {

      return [];

    }

    return catalog.map((option) => ({
      label: option.name.charAt(0).toUpperCase() + option.name.slice(1),
      value: option.key || option.id,
    }));

  }

  /**
   * Obtiene la clase a aplicar al elemento del formulario.
   *
   * @param field - Campo a validar.
   *
   * @returns Clase CSS.
   */
  function getFormControlClassName(field) {

    if (!formik.getFieldMeta(field).touched) {

      return "";

    }

    return formik.getFieldMeta(field)?.error ? "has-error" : "is-valid";

  }

  /**
   * Carga la estructura del modal perfil completado.
   *
   * @returns Estructura del modal perfil completado.
   */
  function renderModal() {

    if (!open) {

      return false;

    }

    return (
      <ClayModal center disableAutoClose observer={observer} size="lg">

        <div className="row mt-4">

          <div className="col-3 px-0 d-none d-md-flex">
            <hr className="mt-3" />
          </div>

          <div className="col col-md-4 text-center">
            <span className="text modal-title font-weight-semi-bold">

              {translate("schedule-appointment-modal")}

            </span>
          </div>

          <div className="col-3 px-0 d-none d-md-flex">
            <hr className="mt-3" />
          </div>

        </div>

        <ClayModal.Body className="px-4 px-lg-5">

          <div className="row mt-4">

            <div className="col-12 col-md-auto align-content-center">

              <span className="text-3 font-weight-semi-bold text-primary">
                {`${translate("day")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                {formatDate(toDate(localSlot.rawDate))}
              </span>

            </div>

            <div className="col-12 col-md-auto pl-md-0 align-content-center mt-2 mt-md-0">

              <span className="text-3 font-weight-semi-bold text-primary">
                {`${translate("hour")}: `}
              </span>

              <ClayButton
                size="sm"
                displayType="info"
                className="text-3 font-weight-light py-2 ml-1 btn-appointment"
              >

                {new Date(localSlot.rawDate).getUTCHours()}
                :
                {new Date(localSlot.rawDate).getUTCMinutes().toString().padStart(2, "0")}

              </ClayButton>

            </div>
          </div>

          <div className="row d-flex mt-3">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("patient-name")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                { `${formik.values.firstName} ${formik.values.firstSurname}` }
              </span>

            </div>
          </div>

          <div className="row d-flex mt-1">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("year-of-birth")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                { formik.values.birthDate }
              </span>

            </div>
          </div>

          <div className="row d-flex mt-1">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("age")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                {Math.floor((new Date() - new Date(formik.values.birthDate)) / 31557600000)}
              </span>

              <span className="text-3 font-weight-light">
                {` ${translate("years")}`}
              </span>

            </div>
          </div>

          <div className="row justify-content-center align-items-center w-100 mt-3">

            <div className="col-2 px-0 d-none d-md-flex">
              <hr className="mt-3" />
            </div>

            <div className="col col-md-7 text-center">

              <span className="text modal-title font-weight-semi-bold">
                {translate("scheduled-with")}
              </span>

            </div>

            <div className="col-2 px-0 d-none d-md-flex">
              <hr className="mt-3" />
            </div>

          </div>

          <div className="row mt-4">
            <div className="col">

              <h5 className="text-primary font-weight-semi-bold">
                {`${extraInfo.doctorData.prefix.name} ${appointmentData.creator.name}` }
              </h5>

            </div>
          </div>

          <div className="row mt-0 mt-md-1">
            <div className="col">

              <h6 className="font-weight-semi-bold">
                { extraInfo.doctorData.medicalSpecialities.map((spec) => spec.name).join(" - ") }
              </h6>

            </div>
          </div>

          <div className="row mt-1">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("consultation-value-simple")}: `}
              </span>

              <span className="text-3 font-weight-light">
                {extraInfo.doctorData.bankAccounts[0].currency.toUpperCase()}
              </span>

              <span className="text-3 font-weight-light">
                {appointmentData.consultationType?.name === "Online"
                  ? (` ${extraInfo.onlinePrice}`) : (` ${extraInfo.price}`)}
              </span>

            </div>
          </div>

          <div className="row mt-1">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("payment-method")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                { paymentMethods.find((method) => method.key === formik.values.paymentMethod).name }
              </span>

            </div>
          </div>

          <div className="row d-flex mt-1">
            <div className="col">

              <span className="text-3 font-weight-semi-bold">
                {`${translate("consultation-type")}: `}
              </span>

              <span className="text-3 ml-1 font-weight-light">
                {appointmentData.consultationType?.name}
              </span>

            </div>
          </div>

          { appointmentData.consultationType?.key === "inPerson" ? (

            <div className="row d-flex mt-1">
              <div className="col">

                <span className="text-3 font-weight-semi-bold">
                  {`${translate("address")}: `}
                </span>

                <span className="text-3 ml-1 font-weight-light">

                  {appointmentData.r_officeAgendaConfiguration_c_doctorsOffice.address}

                </span>

              </div>
            </div>

          ) : (

            <>
            </>

          )}

          <span className="text-2 mt-4 d-flex justify-content-center text-gray-800">

            {`${translate("appointment-email")}`}

          </span>

          <div className="row justify-content-center pb-4 pb-md-0 mt-4">

            <div className="col-12 col-md-auto">

              <ClayButton
                block
                className="px-5"
                displayType="secondary"
                onClick={() => onOpenChange(false)}
              >

                {translate("go-back")}

              </ClayButton>

            </div>

            <div className="col-12 col-md-auto mt-2 mt-md-0">

              <ClayButton
                block
                className="px-5"
                displayType="primary"
                disabled={!formik.isValid || spinner || loading}
                onClick={() => formik.submitForm()}
              >

                {!spinner ? translate("confirm")

                  : (
                    <>

                      <span className="mr-2">
                        {translate("processing")}
                      </span>

                      <ClayIcon
                        className="spinning-animation"
                        spritemap={Liferay.Icons.spritemap}
                        symbol="reload"
                      />

                    </>
                  )}

              </ClayButton>

            </div>
          </div>

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Carga la estructura del modal de confirmación.
   *
   * @returns Estructura del modal de confirmación.
   */
  function renderModalConfirmation() {

    return openConfirmation && (

      <ClayModal center observer={observerConfirmation} size="lg">

        <ClayButton
          onClick={() => onOpenChangeConfirmation(false)}
          displayType="white"
          className="d-flex justify-content-end pb-0 pt-3"
        >
          <ClayIcon className="text-primary" symbol="times" spritemap={Liferay.Icons.spritemap} />
        </ClayButton>

        <div
          className="row justify-content-center align-items-center mt-4"
        >

          <div className="col-2 d-none d-md-block pl-2">
            <hr />
          </div>

          <div className="col custom-close px-md-2 text-center">
            <span className="modal-title text-wrap text-break">

              {translate("appointment-confirmation")}

            </span>
          </div>

          <div className="col-2 d-none d-md-block pr-2">
            <hr />
          </div>

        </div>

        <ClayModal.Body className="mt-4 pb-2 px-4 px-lg-5 justify-content-center">

          <div className="row">
            <div className="col justify-content-center">

              <p className="font-weight-light mb-2 text-center">
                {translate("appointment-confirmation-description")}
              </p>

            </div>
          </div>

          <div className="row mt-2">
            <div className="col justify-content-center">

              <p className="font-weight-light text-center mb-2">
                {translate("appointment-confirmation-description-2")}
              </p>

            </div>
          </div>

          <div className="row justify-content-center my-4">

            <div className="col-12 col-md-auto">

              <ClayButton
                block
                displayType="secondary"
                size="lg"
                onClick={() => {

                  onOpenChangeConfirmation(false);
                  Liferay.SPA.app.navigate("/web/dr-now-user-private/my-appointments");

                }}
              >

                <span className="text-primary">
                  {translate("appointment-confirmation-button-backward")}
                </span>

              </ClayButton>

            </div>

            <div className="col-12 col-md-auto mt-2 mt-md-0">

              <ClayButton
                block
                displayType="primary"
                size="lg"
                onClick={() => {

                  onOpenChangeConfirmation(false);
                  Liferay.SPA.app.navigate("/web/dr-now-user-private/home");

                }}
              >

                {translate("appointment-confirmation-button-forward")}

              </ClayButton>

            </div>

          </div>

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Método que muestra el mensaje de error de un campo.
   *
   * @param fieldName - Nombre del campo.
   *
   * @returns Componente con el mensaje de error del campo.
   */
  function renderFieldErrorMessage(fieldName) {

    if (formik.getFieldMeta(fieldName).touched && formik.errors?.[fieldName]) {

      return (
        <div className="form-feedback-item">

          <ClayIcon
            className="text-2 text-danger mr-1"
            spritemap={Liferay.Icons.spritemap}
            symbol="dr-now-bell"
          />

          {formik.errors[fieldName]}

        </div>
      );

    }

    return null;

  }

  /**
   * Renderiza el formulario de datos del médico.
   *
   * @returns Component con Formulario.
   */
  function renderDoctorForm() {

    return (
      <>

        <div className="row mb-2">
          <div className="col text-center">

            <h3 className="font-weight-semi-bold text-primary">
              {translate("schedule-appointment")}
            </h3>

          </div>
        </div>

        <div className="row mb-2">
          <div className="col text-center font-weight-light">

            <p className="text-dark">
              {translate("appointment-question")}
            </p>

          </div>
        </div>

        <div className="row">
          <div className="col d-flex justify-content-center">

            <ClayForm.Group className={getFormControlClassName("personalConsultation")}>

              <ClayRadioGroup
                onChange={(value) => {

                  formik.resetForm();
                  formik.setFieldValue("personalConsultation", value);

                }}
                defaultValue={!!(persistentData.personalConsultation === undefined
                  || persistentData.personalConsultation)}
                inline
              >
                <ClayRadio label={translate("yes")} value />
                <ClayRadio label={translate("no")} value={false} />

              </ClayRadioGroup>

              <div className="form-feedback-group">

                {renderFieldErrorMessage("personalConsultation")}

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">

            <ClayForm.Group className={getFormControlClassName("firstName")}>

              <label className="form-control-label" htmlFor="firstName">

                {translate("first-name")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="firstName"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate(
                  "name-placeholder",
                )}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("firstName")}

              </div>

            </ClayForm.Group>

          </div>

          <div className="col-md-6">

            <ClayForm.Group className={getFormControlClassName("firstSurname")}>

              <label className="form-control-label" htmlFor="firstSurname">

                {translate("first-surname")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="firstSurname"
                name="firstSurname"
                value={formik.values.firstSurname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("surname-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">
                {renderFieldErrorMessage("firstSurname")}
              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">

            <ClayForm.Group className={getFormControlClassName("gender")}>

              <label className="form-control-label" htmlFor="gender">

                {translate("gender")}

                <span className="text-danger">*</span>

              </label>

              <ClaySelectWithOption
                id="gender"
                name="gender"
                className="text-dark font-weight-light"
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                options={[{
                  label: translate("gender-placeholder"),
                  value: "",
                  disabled: true,
                  hidden: true,
                }].concat(catalogsMapper(genders))}
                required
              />

              <div className="form-feedback-group">
                {renderFieldErrorMessage("gender")}
              </div>

            </ClayForm.Group>

          </div>

          <div className="col-lg-6">

            <ClayForm.Group className={getFormControlClassName("birthDate")}>

              <label className="form-control-label" htmlFor="birthDate">
                {translate("birth-date")}
              </label>

              <ClayDatePicker
                id="birthDate"
                name="birthDate"
                onChange={(date) => {

                  if (REGEXP.DATE.test(date)) {

                    formik.setFieldValue("birthDate", date, true);

                  } else {

                    formik.setFieldValue("birthDate", "", true);

                  }

                }}
                onBlur={formik.handleBlur}
                placeholder={translate("birth-date-placeholder")}
                spritemap={Liferay.Icons.spritemap}
                value={formik.values.birthDate}
                inputName="birthDate"
                dateFormat="MM/dd/yyyy"
                months={MONTHS}
                weekdaysShort={DAYS}
                years={{
                  end: new Date().getFullYear(),
                  start: new Date().getFullYear() - 130,
                }}
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("birthDate")}
              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">

            <ClayForm.Group className={getFormControlClassName("email")}>

              <label className="form-control-label" htmlFor="email">

                {translate("email")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("email-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("email")}

              </div>

            </ClayForm.Group>

          </div>

          <div className="col-lg-6">

            <div className="row">

              <div className="col-5">

                <ClayForm.Group className={getFormControlClassName("countryCode")}>

                  <label className="form-control-label" htmlFor="countryCode">

                    {translate("country-code")}

                    <span className="text-danger">*</span>

                  </label>

                  <CountrySelect formik={formik} countries={countries} loading={loading} />

                </ClayForm.Group>

              </div>

              <div className="col">

                <ClayForm.Group className={getFormControlClassName("phoneNumber")}>

                  <label className="form-control-label" htmlFor="phoneNumber">

                    {translate("phone-number")}

                    <span className="text-danger">*</span>

                  </label>

                  <ClayInput
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={translate("phone-number-placeholder")}
                    autoComplete="off"
                    type="text"
                  />

                  <div className="form-feedback-group">

                    {renderFieldErrorMessage("phoneNumber")}

                  </div>

                </ClayForm.Group>

              </div>

            </div>

          </div>

        </div>

        <div className="row">
          <div className="col-12">

            {formik.values.personalConsultation ? (

              ""

            ) : (

              <ClayForm.Group className={getFormControlClassName("bondWithPatient")}>

                <label className="form-control-label" htmlFor="bondWithPatient">

                  {translate("bond-with-the-patient")}

                  <span className="text-danger">*</span>

                </label>

                <ClaySelectWithOption
                  id="bondWithPatient"
                  name="bondWithPatient"
                  value={formik.values.bondWithPatient}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  options={[{
                    label: translate("bond-with-the-patient-placeholder"),
                    value: "",
                    disabled: true,
                    hidden: true,
                  }].concat(catalogsMapper(relationships))}
                  required
                />

                <div className="form-feedback-group">

                  {renderFieldErrorMessage("bondWithPatient")}

                </div>

              </ClayForm.Group>

            )}
          </div>
        </div>

        <div className="row my-4">
          <div className="col text-center">

            <h3 className="font-weight-semi-bold text-primary">
              {translate("reason-for-consultation")}
            </h3>

          </div>
        </div>

        <div className="row">
          <div className="col-12">

            <ClayForm.Group className={getFormControlClassName("message")}>

              <label className="form-control-label" htmlFor="message">

                {translate("leave-your-message")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="message"
                name="message"
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("leave-your-message-placeholder")}
                autoComplete="off"
                component="textarea"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("message")}

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">
          <div className="col-12">

            <ClayForm.Group className={getFormControlClassName("paymentMethod")}>

              <label className="form-control-label" htmlFor="paymentMethod">

                {translate("payment-method")}

                <span className="text-danger">*</span>

              </label>

              <ClaySelectWithOption
                id="paymentMethod"
                name="paymentMethod"
                className="text-dark font-weight-light"
                value={formik.values.paymentMethod}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                options={[{
                  label: translate("payment-method-placeholder"),
                  value: "",
                  disabled: true,
                  hidden: true,
                }].concat(catalogsMapper(paymentMethods))}
                required
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("paymentMethod")}

              </div>

            </ClayForm.Group>

          </div>
        </div>

      </>
    );

  }

  /**
   * Renderiza la sección de botones.
   *
   * @returns Componente React.
   */
  function renderButtonsSection() {

    return (
      <div className="row justify-content-center pb-4 pb-md-0 mt-4">

        <div className="col-12 col-md-auto">

          <ClayButton
            displayType="secondary"
            size="lg"
            block
            onClick={() => {

              setPersistentData(formik.values);

              setStep(0);

            }}
          >
            {translate("go-back")}
          </ClayButton>

        </div>

        <div className="col-12 col-md-auto mt-2 mt-md-0">

          <ClayButton
            displayType="primary"
            size="lg"
            disabled={!formik.isValid || spinner || loading}
            block
            onClick={() => {

              onOpenChange(true);

            }}
          >
            {translate("schedule-appointment-modal")}
          </ClayButton>

        </div>

      </div>
    );

  }

  // ===============================
  // Función de carga del componente
  // ===============================

  useEffect(() => {

    if (!Liferay || !Liferay.SPA) {

      return;

    }

    (async () => {

      formik.setValues(persistentData);

      try {

        setLoading(true);

        const [
          countriesCatalogResponse,
          gendersCatalogResponse,
          paymentMethodsResponse,
          relationshipsCatalogResponse,
        ] = await Promise.all([
          ApiClient.getCountries(),
          ApiClient.getPicklist("DR_NOW_GENDERS_PICKLIST"),
          ApiClient.getPicklist("DR_NOW_PAYMENT_METHODS_PICKLIST"),
          ApiClient.getPicklist("DR_NOW_RELATIONSHIPS_PICKLIST"),
        ]);

        if (appointmentData.consultationType?.name === "Online") {

          const paymentMethodsResponseFiltered = paymentMethodsResponse.data.listTypeEntries
            .filter((entry) => entry.externalReferenceCode === "DRNOW_PAYMENT_METHOD_ONLINE");

          paymentMethodsResponse.data.listTypeEntries = paymentMethodsResponseFiltered;

        }

        const countriesWithoutFlags = countriesCatalogResponse.data?.items;

        const countriesWithFlags = countriesWithoutFlags.map((country) => ({
          ...country,
          flag: flags[country.a2],
        }));

        setCountries(countriesWithFlags);
        setGenders(gendersCatalogResponse.data.listTypeEntries);
        setPaymentMethods(paymentMethodsResponse.data.listTypeEntries);
        setRelationships(relationshipsCatalogResponse.data.listTypeEntries);

        if (persistentData.personalConsultation === undefined) {

          const { phoneNumber } = extraInfo.user.userAccountContactInformation.telephones[0];

          const formattedBirthDate = formatInTimeZone(
            toDate(extraInfo.user.birthDate),
            "UTC",
            "MM/dd/yyyy",
          );

          const phoneCountry = countriesWithFlags.find(
            (item) => item.idd === parseInt(phoneNumber.split("-")[0], 10),
          );

          formik.setFieldValue("personalConsultation", true);
          formik.setFieldValue("birthDate", formattedBirthDate);
          formik.setFieldValue("email", extraInfo.user.emailAddress);
          formik.setFieldValue("firstName", extraInfo.user.givenName);
          formik.setFieldValue("phoneNumber", phoneNumber.split("-")[1]);
          formik.setFieldValue("firstSurname", extraInfo.user.familyName);
          formik.setFieldValue("gender", extraInfo.user.drNowUser[0].gender.key);
          formik.setFieldValue("countryCode", `${phoneCountry.a2} ${phoneCountry.idd}`);

        }

      } catch {

        Liferay.Util.openToast({
          title: "",
          message: `
            <h4 class='font-weight-medium m-0'>
              ${translate("dr-now-doctor-sign-up-service-error")}
            </h4>
            <span class='text-3'>
              ${translate("dr-now-doctor-sign-up-please-try-again-later")}
            </span>
          `,
          type: "danger",
          toastProps: {
            hideCloseIcon: true,
          },
        });

      } finally {

        setLoading(false);

      }

    })();

  }, []);

  // =======================
  // Template del componente
  // =======================

  return (

    <div className="col px-lg-7 px-0">

      <form onSubmit={formik.handleSubmit}>

        <div className="row mb-4">
          <div className="col">{renderDoctorForm()}</div>
        </div>

        <div className="row mb-4">
          <div className="col">{renderButtonsSection()}</div>
        </div>

        {renderModal()}

        {renderModalConfirmation()}

      </form>

    </div>

  );

}

export default AppointmentForm;
